import React, { Component } from "react";
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Platform,
  Alert,
} from "react-native";
import { MaterialIcons, MaterialCommunityIcons } from "@expo/vector-icons";
import colors from "../constants/colors";
import {
  renderLocationLabel,
  findLocationInList,
  parseScannedValue,
  cleanScannedText,
  normalizeWedgeScanText,
} from "../utils/locationScan";
import { validateDecimals } from "../utils";
import { isMobileWeb } from "../utils/mobileWeb";
import BarcodeScannerComponent from "./widgets/BarCodeScannerComponent";
import {
  LOCATION_LEVELS,
  locationKey,
  locationMatchesSearch,
  unwrapLocationValue,
  isSameLocation,
  getPartId,
  getDeepestLevelIndex,
  trimLocationToLevel,
} from "../utils/inventoryTransferLocationTree";

export function getTransferLocationLabel(location) {
  if (!location) return "";
  if (location === "default_location") return "Default Location";
  return renderLocationLabel(location) || location?.site?.locationName || "";
}

export function getTransferLocationPathLabel(location) {
  if (!location) return "";
  if (typeof location === "string") {
    if (location === "default_location") return "Default Location";
    return location;
  }
  const parts = [];
  LOCATION_LEVELS.forEach((key) => {
    const name = location?.[key]?.locationName;
    if (name) parts.push(String(name).trim());
  });
  if (parts.length) return parts.join(" > ");
  return getTransferLocationLabel(location);
}

/**
 * Map historial / raw location onto a catalog entry so table pills can display it.
 * Mirrors form.filterLocations for default_location and path matching.
 */
export function resolveTransferLocation(location, inventoryLocations) {
  if (!location) return null;
  const all = Array.isArray(inventoryLocations) ? inventoryLocations : [];
  if (!all.length) {
    return typeof location === "string" || typeof location === "object" ? location : null;
  }

  if (location === "default_location") {
    const defaults = all.filter((l) => l?.site?.defaultLocation);
    return defaults[0] || location;
  }

  const unwrapped = unwrapLocationValue(location) || (typeof location === "object" ? location : null);
  if (!unwrapped || typeof unwrapped !== "object") {
    return typeof location === "string" ? location : null;
  }

  const deepest = getDeepestLevelIndex(unwrapped);
  if (deepest < 0) {
    if (unwrapped?.site?.defaultLocation) {
      const defaults = all.filter((l) => l?.site?.defaultLocation);
      return defaults[0] || unwrapped;
    }
    return unwrapped;
  }

  const match = all.find((loc) => {
    for (let i = 0; i <= deepest; i++) {
      const key = LOCATION_LEVELS[i];
      if (getPartId(loc?.[key]) !== getPartId(unwrapped?.[key])) return false;
    }
    return true;
  });
  if (match) return trimLocationToLevel(match, deepest);

  const deepKey = LOCATION_LEVELS[deepest];
  const deepId = getPartId(unwrapped[deepKey]);
  const byId = all.find((loc) => getPartId(loc?.[deepKey]) === deepId);
  return byId ? trimLocationToLevel(byId, deepest) : unwrapped;
}

export function getTransferToLocationOptions(inventoryLocations, search) {
  const all = Array.isArray(inventoryLocations) ? inventoryLocations : [];
  const q = (search || "").trim().toLowerCase();
  if (!q) return all;
  return all.filter((loc) => locationMatchesSearch(loc, q));
}

export function getToLocationForLot(lot, fallbackToLocation) {
  if (!lot) return null;
  if (lot.toLocation) return lot.toLocation;
  return unwrapLocationValue(fallbackToLocation) || null;
}

/** One submission row per selected lot; each lot has its own from/to location. */
export function buildInventoryTransferSaveRows(selectedLots, sharedFromLocation, inventoryLocations) {
  const list = Array.isArray(selectedLots) ? selectedLots : [];
  return list
    .filter((lot) => !(lot.isSeed && !lot.lot))
    .filter((lot) => lot.item || lot.itemName)
    .map((lot) => {
      const name = lot.item || lot.itemName || "";
      const amt = parseFloat(lot.editedAmount);
      const qty = !isNaN(amt) ? amt : 0;
      const rawFrom = sharedFromLocation || lot.location || null;
      const fromLoc = resolveTransferLocation(rawFrom, inventoryLocations) || rawFrom || null;
      const toLoc = getToLocationForLot(lot);
      const historial = (lot.historial || []).map((h, idx) => {
        if (idx === 0) {
          return {
            ...h,
            amount: !isNaN(amt) ? amt : h.amount,
            editedAmount: !isNaN(amt) ? amt : h.editedAmount,
            location: fromLoc || lot.location || h.location,
          };
        }
        return h;
      });
      const lotObj = {
        lot: lot.lot,
        batchId: lot.batchId || null,
        historial: historial.length
          ? historial
          : [
              {
                amount: !isNaN(amt) ? amt : 0,
                editedAmount: !isNaN(amt) ? amt : 0,
                location: fromLoc || lot.location,
                units: lot.units,
              },
            ],
        units: lot.units,
        item: name,
        editedAmount: !isNaN(amt) ? amt : 0,
        toLocation: toLoc || null,
      };
      return {
        item: {
          item: name,
          itemName: name,
          type: lot.type || "",
          inventoryType: lot.inventoryType || "",
          _id: lot._id,
          productCode: lot.productCode || "",
          units: lot.units || "",
        },
        fromLocation: fromLoc ? [fromLoc] : [""],
        toLocation: toLoc ? [toLoc] : [""],
        lotcode: [lotObj],
        quantity:
          qty % 1 !== 0 && !validateDecimals(String(qty))
            ? Number(qty.toFixed(3))
            : qty,
      };
    });
}

export function findLotMissingToLocation(selectedLots, fallbackToLocation) {
  const list = Array.isArray(selectedLots) ? selectedLots : [];
  return (
    list
      .filter((l) => !l.isSeed || l.lot)
      .find((lot) => !getToLocationForLot(lot, fallbackToLocation)) || null
  );
}

export function locationsRoughlyMatch(a, b) {
  if (!a || !b) return false;
  if (a === "default_location" || b === "default_location") {
    const other = a === "default_location" ? b : a;
    if (other === "default_location") return true;
    return !!(other?.site?.defaultLocation);
  }
  if (isSameLocation(a, b)) return true;
  const ka = locationKey(a);
  const kb = locationKey(b);
  if (ka && kb && ka === kb) return true;
  const la = getTransferLocationLabel(a);
  const lb = getTransferLocationLabel(b);
  return !!(la && lb && la === lb);
}

/**
 * Shared selected-lots table + single Transfer details box (per selected lot).
 * Controlled by parent state.
 */
class InventoryTransferLotsPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showLotCodesTip: false,
      // Banner stays until a specific lot code is selected
      showLotCodesBanner: !!props.showAllLotCodesInfo && !props.selectedLotKey,
      scanToLocation: false,
    };
    this._lotCodesTipTimer = null;
    this._handlingToLocationScan = false;
    this._toLocationScanInterval = null;
  }

  componentDidMount() {
    if (this.props.showAllLotCodesInfo && this.props.selectedItemName && !this.props.selectedLotKey) {
      this.showLotCodesTipTemporarily();
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.props.showAllLotCodesInfo) return;

    const lotSelected = !!this.props.selectedLotKey;
    const prevLotSelected = !!prevProps.selectedLotKey;
    const itemSelected = !!this.props.selectedItemName;
    const itemChanged =
      itemSelected && this.props.selectedItemName !== prevProps.selectedItemName;
    const returnedToItemMode = itemSelected && !lotSelected && prevLotSelected;
    const lotsJustAdded =
      (prevProps.selectedLots || []).length === 0 && (this.props.selectedLots || []).length > 0;

    if (lotSelected && this.props.selectedLotKey !== prevProps.selectedLotKey) {
      this.clearLotCodesTipTimer();
      if (this.state.showLotCodesBanner || this.state.showLotCodesTip) {
        this.setState({ showLotCodesBanner: false, showLotCodesTip: false });
      }
      return;
    }

    if (itemChanged || returnedToItemMode || lotsJustAdded) {
      if (!this.state.showLotCodesBanner) {
        this.setState({ showLotCodesBanner: true });
      }
      this.showLotCodesTipTemporarily();
    }
  }

  componentWillUnmount() {
    this.clearLotCodesTipTimer();
    if (this._toLocationScanInterval) {
      clearTimeout(this._toLocationScanInterval);
      this._toLocationScanInterval = null;
    }
  }

  clearLotCodesTipTimer() {
    if (this._lotCodesTipTimer) {
      clearTimeout(this._lotCodesTipTimer);
      this._lotCodesTipTimer = null;
    }
  }

  showLotCodesTipTemporarily() {
    this.clearLotCodesTipTimer();
    this.setState({ showLotCodesTip: true });
    this._lotCodesTipTimer = setTimeout(() => {
      this._lotCodesTipTimer = null;
      this.setState({ showLotCodesTip: false });
    }, 5000);
  }

  toggleLotCodesTip() {
    const next = !this.state.showLotCodesTip;
    this.clearLotCodesTipTimer();
    this.setState({ showLotCodesTip: next });
    if (next) {
      this._lotCodesTipTimer = setTimeout(() => {
        this._lotCodesTipTimer = null;
        this.setState({ showLotCodesTip: false });
      }, 5000);
    }
  }

  /** Native + mobile web: camera scan for Transfer to (matches Transfer by Location). */
  canShowToLocationCamera() {
    if (this.props.readOnly) return false;
    return Platform.OS !== "web" || isMobileWeb();
  }

  showToLocationScanner(open) {
    this.setState({ scanToLocation: !!open });
  }

  resolveToLocationForScan(raw) {
    const options = Array.isArray(this.props.inventoryLocations)
      ? this.props.inventoryLocations
      : [];
    if (!options.length) return null;

    const candidates = [];
    const seen = new Set();
    const addCandidate = (text) => {
      const clean = cleanScannedText(parseScannedValue(text));
      if (!clean || seen.has(clean)) return;
      seen.add(clean);
      candidates.push(clean);
      const normalized = normalizeWedgeScanText(clean);
      if (normalized && normalized !== clean && !seen.has(normalized)) {
        seen.add(normalized);
        candidates.push(normalized);
      }
    };
    addCandidate(raw);

    for (let i = 0; i < candidates.length; i++) {
      const text = candidates[i];
      const match =
        findLocationInList(text, options, { exactOnly: true }) ||
        findLocationInList(text, options, { exactOnly: false });
      if (match) return match;
    }
    return null;
  }

  applyScannedToLocation(location) {
    const {
      selectedItemName,
      selectedLotKey,
      onSelectLotKey,
      onSelectToLocation,
      onToggleToLocationDropdown,
      onChangeToLocationSearch,
    } = this.props;
    const lot = this.getSelectedLot();
    const itemLots = selectedItemName ? this.getLotsForItem(selectedItemName) : [];
    const itemMode = !!(selectedItemName && itemLots.length);
    if (!itemMode && !lot && !selectedLotKey) return false;
    if (!onSelectToLocation) return false;

    let selected;
    if (itemMode) {
      selected = onSelectToLocation(location, null, selectedItemName);
    } else {
      const key = lot?.key || selectedLotKey;
      if (key && onSelectLotKey) onSelectLotKey(key);
      selected = onSelectToLocation(location, key);
    }
    if (selected === false) {
      if (onToggleToLocationDropdown) onToggleToLocationDropdown(true);
      return false;
    }
    if (onChangeToLocationSearch) onChangeToLocationSearch("");
    if (onToggleToLocationDropdown) onToggleToLocationDropdown(false);
    return true;
  }

  finishToLocationScan(data) {
    const clean = normalizeWedgeScanText(cleanScannedText(parseScannedValue(data)));
    if (!clean) return;
    const location = this.resolveToLocationForScan(data);
    if (location) {
      this.applyScannedToLocation(location);
      return;
    }
    Alert.alert("", "This location does not exist.", [{ text: "OK" }]);
    if (this.props.onChangeToLocationSearch) {
      this.props.onChangeToLocationSearch(clean);
    }
    if (this.props.onToggleToLocationDropdown) {
      this.props.onToggleToLocationDropdown(true);
    }
  }

  handleToLocationBarCodeScanned(raw) {
    const data = parseScannedValue(raw);
    if (this._handlingToLocationScan) return;
    this._handlingToLocationScan = true;
    this.showToLocationScanner(false);
    setTimeout(() => {
      this._handlingToLocationScan = false;
      this.finishToLocationScan(data);
    }, 250);
  }

  /**
   * Laser/wedge or pasted barcode in the Transfer to search field:
   * resolve only on exact/path barcode matches so typing still filters the list.
   */
  onToLocationSearchChange(val) {
    const { onChangeToLocationSearch } = this.props;
    const cleanVal = cleanScannedText(val);
    if (onChangeToLocationSearch) onChangeToLocationSearch(cleanVal);
    if (this._toLocationScanInterval) clearTimeout(this._toLocationScanInterval);
    if (!cleanVal) return;
    const looksLikeBarcode = /[-/]/.test(cleanVal) || cleanVal.length >= 8;
    if (!looksLikeBarcode) return;
    this._toLocationScanInterval = setTimeout(() => {
      this._toLocationScanInterval = null;
      const options = Array.isArray(this.props.inventoryLocations)
        ? this.props.inventoryLocations
        : [];
      const location =
        findLocationInList(cleanVal, options, { exactOnly: true }) ||
        findLocationInList(normalizeWedgeScanText(cleanVal), options, { exactOnly: true });
      if (location) this.applyScannedToLocation(location);
    }, 450);
  }

  getGroupedSelected() {
    const groups = {};
    (this.props.selectedLots || []).forEach((lot) => {
      const name = lot.item || lot.itemName || "";
      if (!groups[name]) groups[name] = [];
      groups[name].push(lot);
    });
    return groups;
  }

  getSelectedLot() {
    const { selectedLots, selectedLotKey } = this.props;
    if (!selectedLotKey) return null;
    return (selectedLots || []).find((l) => l.key === selectedLotKey) || null;
  }

  getLotsForItem(itemName) {
    if (!itemName) return [];
    return (this.props.selectedLots || []).filter(
      (l) => (l.item || l.itemName || "") === itemName
    );
  }

  getCommonToLocation(lots, fallbackToLocation) {
    const list = Array.isArray(lots) ? lots : [];
    if (!list.length) return unwrapLocationValue(fallbackToLocation) || null;
    const first = getToLocationForLot(list[0], fallbackToLocation);
    if (!first) return null;
    const firstKey = locationKey(first) || getTransferLocationPathLabel(first);
    const allSame = list.every((lot) => {
      const to = getToLocationForLot(lot, fallbackToLocation);
      if (!to) return false;
      const key = locationKey(to) || getTransferLocationPathLabel(to);
      return key && key === firstKey;
    });
    return allSame ? first : null;
  }

  getCurrentLocationLabel(lot) {
    if (!lot) return "Select location";
    if (lot.locationLabel) return lot.locationLabel;
    if (lot.location) return getTransferLocationPathLabel(lot.location);
    if (this.props.sharedFromLocationLabel) return this.props.sharedFromLocationLabel;
    return "Select location";
  }

  getItemFromLocationLabel(lots) {
    if (this.props.sharedFromLocationLabel) return this.props.sharedFromLocationLabel;
    const list = Array.isArray(lots) ? lots : [];
    if (!list.length) return "Select location";
    const first = this.getCurrentLocationLabel(list[0]);
    const allSame = list.every((lot) => this.getCurrentLocationLabel(lot) === first);
    return allSame ? first : first || "Select location";
  }

  renderAllLotCodesInfo() {
    if (!this.props.showAllLotCodesInfo || !this.state.showLotCodesBanner) return null;
    const { showLotCodesTip } = this.state;
    return (
      <View style={styles.lotCodesInfoWrap}>
        <View style={styles.lotCodesInfoBanner}>
          <Text style={styles.lotCodesInfoTxt}>
            All lot codes for this item will be included in the transfer.
          </Text>
          <TouchableOpacity
            onPress={() => this.toggleLotCodesTip()}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel="Lot codes transfer help"
          >
            <MaterialIcons name="info-outline" size={18} color={colors.primary} />
          </TouchableOpacity>
        </View>
        {showLotCodesTip ? (
          <View style={styles.lotCodesTipBubble} pointerEvents="box-none">
            <View style={styles.lotCodesTipArrow} />
            <Text style={styles.lotCodesTipTxt}>
              To transfer specific lot codes only, tap the item to expand and select the lot codes
              you want.
            </Text>
          </View>
        ) : null}
      </View>
    );
  }

  renderTransferDetails() {
    const {
      selectedItemName,
      showToLocationDropdown,
      toLocationSearch,
      inventoryLocations,
      readOnly,
      fallbackToLocation,
      onSelectLotKey,
      onToggleToLocationDropdown,
      onSelectToLocation,
    } = this.props;
    const lot = this.getSelectedLot();
    const itemLots = selectedItemName ? this.getLotsForItem(selectedItemName) : [];
    const itemMode = !!(selectedItemName && itemLots.length);
    if (!itemMode && !lot) return null;

    const itemName = itemMode
      ? selectedItemName
      : lot.item || lot.itemName || "";
    const productCode = itemMode
      ? itemLots.find((l) => l.productCode)?.productCode || ""
      : "";
    const detailsTitle = itemMode
      ? `Transfer details > (${itemName})`
      : `Transfer details > (${itemName}${lot.lot ? ` / ${lot.lot}` : " / No Lot"})`;
    const fromLabel = itemMode
      ? this.getItemFromLocationLabel(itemLots)
      : this.getCurrentLocationLabel(lot);
    const toLocation = itemMode
      ? this.getCommonToLocation(itemLots, fallbackToLocation)
      : getToLocationForLot(lot, fallbackToLocation);
    const toLabel = getTransferLocationPathLabel(toLocation) || "Select location";
    const dropdownOpen = !readOnly && showToLocationDropdown;
    const options = getTransferToLocationOptions(inventoryLocations, toLocationSearch);
    const showCamera = this.canShowToLocationCamera();
    const { scanToLocation } = this.state;

    return (
      <View style={styles.detailsBox}>
        {scanToLocation && showCamera ? (
          <BarcodeScannerComponent
            onBarCodeScanned={(e) => this.handleToLocationBarCodeScanned(e)}
            closeScanner={() => this.showToLocationScanner(false)}
          />
        ) : null}
        <Text style={styles.detailsTitle}>{detailsTitle}</Text>
        <View style={styles.detailsRow}>
          <View style={styles.detailsCol}>
            <MaterialIcons name="location-on" size={18} color={colors.primary} />
            <Text style={styles.detailsLabel}>Current Location</Text>
            <View style={[styles.detailsChip, styles.detailsChipFilled]}>
              <Text style={styles.detailsChipTxtFilled} numberOfLines={2}>
                {fromLabel}
              </Text>
            </View>
          </View>
          <MaterialIcons
            name="arrow-forward"
            size={28}
            color={colors.primary}
            style={styles.detailsArrow}
          />
          <View style={styles.detailsCol}>
            <MaterialIcons name="location-on" size={18} color={colors.primary} />
            <Text style={styles.detailsLabel}>Transfer to</Text>
            <TouchableOpacity
              style={styles.detailsChip}
              disabled={!!readOnly}
              onPress={() => {
                if (!itemMode && onSelectLotKey) onSelectLotKey(lot.key);
                if (onToggleToLocationDropdown) onToggleToLocationDropdown(!dropdownOpen);
              }}
            >
              <Text style={styles.detailsChipTxt} numberOfLines={2}>
                {toLabel}
              </Text>
              {!readOnly ? (
                <MaterialIcons
                  name={dropdownOpen ? "arrow-drop-up" : "arrow-drop-down"}
                  size={20}
                  color={colors.primary}
                />
              ) : null}
            </TouchableOpacity>
            {dropdownOpen ? (
              <View style={styles.toDropdown}>
                <View style={styles.toDropdownSearch}>
                  <MaterialIcons name="search" size={18} color={colors.orange} />
                  <TextInput
                    style={styles.toDropdownInput}
                    value={toLocationSearch || ""}
                    placeholder={showCamera ? "Scan or search transfer to…" : "Search transfer to…"}
                    placeholderTextColor="#939598"
                    onChangeText={(val) => this.onToLocationSearchChange(val)}
                    autoFocus
                  />
                  {showCamera ? (
                    <TouchableOpacity
                      onPress={() => this.showToLocationScanner(true)}
                      style={styles.toDropdownCameraBtn}
                      accessibilityLabel="Scan transfer to location with camera"
                    >
                      <MaterialIcons name="photo-camera" color="#fff" size={18} />
                    </TouchableOpacity>
                  ) : null}
                </View>
                <ScrollView
                  style={styles.toDropdownList}
                  nestedScrollEnabled
                  keyboardShouldPersistTaps="handled"
                >
                  {options.length === 0 ? (
                    <Text style={styles.emptyTxt}>No locations found</Text>
                  ) : (
                    options.slice(0, 80).map((loc, idx) => {
                      const label = getTransferLocationLabel(loc);
                      return (
                        <TouchableOpacity
                          key={`${locationKey(loc)}-${idx}`}
                          style={styles.toDropdownRow}
                          onPress={() => {
                            if (!onSelectToLocation) return;
                            if (itemMode) onSelectToLocation(loc, null, itemName);
                            else onSelectToLocation(loc, lot.key);
                          }}
                        >
                          <Text style={styles.toDropdownRowTxt} numberOfLines={2}>
                            {label}
                          </Text>
                        </TouchableOpacity>
                      );
                    })
                  )}
                </ScrollView>
              </View>
            ) : null}
          </View>
        </View>
        {this.props.toLocationError ? (
          <View style={styles.toLocationErrorBanner}>
            <MaterialIcons name="info-outline" size={18} color={colors.primary} style={styles.toLocationErrorIcon} />
            <Text style={styles.toLocationErrorTxt}>{this.props.toLocationError}</Text>
            <TouchableOpacity
              style={styles.toLocationErrorOkBtn}
              onPress={() => this.props.onDismissToLocationError && this.props.onDismissToLocationError()}
            >
              <Text style={styles.toLocationErrorOkTxt}>Ok</Text>
            </TouchableOpacity>
          </View>
        ) : null}
        {this.renderAllLotCodesInfo()}
      </View>
    );
  }

  render() {
    const groups = this.getGroupedSelected();
    const names = Object.keys(groups);
    if (!names.length) return null;
    const {
      selectedLotKey,
      selectedItemName,
      expandedItems,
      readOnly,
      onSelectLotKey,
      onSelectItem,
      onRemoveLot,
      onEditLotAmount,
      onToggleItemExpanded,
    } = this.props;

    return (
      <View style={styles.selectedSection}>
        {names.map((name) => {
          const lots = groups[name];
          const expanded = expandedItems?.[name] === true;
          const itemSelected = selectedItemName === name;
          return (
            <View key={name} style={styles.selectedGroup}>
              <View
                style={[
                  styles.selectedGroupHeader,
                  itemSelected && styles.selectedGroupHeaderActive,
                ]}
              >
                <TouchableOpacity
                  style={styles.selectedGroupTitleBtn}
                  onPress={() => onSelectItem && onSelectItem(name)}
                >
                  <Text
                    onPress={() => onToggleItemExpanded && onToggleItemExpanded(name)}
                    style={[
                      styles.selectedGroupTitle,
                      itemSelected && styles.selectedGroupTitleActive,
                    ]}
                  >
                    {name}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.selectedGroupChevronBtn}
                  onPress={() => onToggleItemExpanded && onToggleItemExpanded(name)}
                >
                  <MaterialIcons
                    name={expanded ? "keyboard-arrow-down" : "keyboard-arrow-right"}
                    size={22}
                    color={colors.primary}
                  />
                </TouchableOpacity>
              </View>
              {expanded ? (
                <View>
                  <View style={styles.lotTableHeader}>
                    <Text style={[styles.lotTh, styles.lotColRemove]} />
                    <Text style={[styles.lotTh, styles.lotColLot]}>Lot Code</Text>
                    <Text style={[styles.lotTh, styles.lotColAmt]}>Amount</Text>
                    <Text style={[styles.lotTh, styles.lotColLoc]}>Location</Text>
                  </View>
                  {lots.map((lot) => {
                    const isSelected = itemSelected || selectedLotKey === lot.key;
                    return (
                      <TouchableOpacity
                        key={lot.key}
                        style={[styles.lotTableRow, isSelected && styles.lotTableRowSelected]}
                        activeOpacity={0.85}
                        onPress={() => onSelectLotKey && onSelectLotKey(lot.key)}
                      >
                        {readOnly ? (
                          <View style={[styles.lotTd, styles.lotColRemove]} />
                        ) : (
                          <TouchableOpacity
                            style={[styles.lotTd, styles.lotColRemove]}
                            onPress={() => onRemoveLot && onRemoveLot(lot.key)}
                          >
                            <MaterialCommunityIcons name="close-circle" size={22} color={colors.quit} />
                          </TouchableOpacity>
                        )}
                        <Text style={[styles.lotTd, styles.lotColLot]} numberOfLines={1}>
                          {lot.lot || "No Lot"}
                        </Text>
                        <View style={[styles.lotTd, styles.lotColAmt]}>
                          <TextInput
                            style={styles.amountInput}
                            value={lot.editedAmount != null ? String(lot.editedAmount) : ""}
                            keyboardType="decimal-pad"
                            editable={!readOnly}
                            onFocus={() => onSelectLotKey && onSelectLotKey(lot.key)}
                            onChangeText={(val) => {
                              if (readOnly) return;
                              if (val !== "" && !validateDecimals(val)) return;
                              if (onEditLotAmount) onEditLotAmount(lot.key, val);
                            }}
                          />
                          {lot.units ? <Text style={styles.unitsTxt}>{lot.units}</Text> : null}
                        </View>
                        <Text style={[styles.lotTd, styles.lotColLoc]} numberOfLines={2}>
                          {lot.locationLabel || this.getCurrentLocationLabel(lot)}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ) : null}
            </View>
          );
        })}
        {this.renderTransferDetails()}
      </View>
    );
  }
}

export default InventoryTransferLotsPanel;

const styles = StyleSheet.create({
  selectedSection: { marginBottom: 12 },
  selectedGroup: { marginBottom: 8 },
  selectedGroupHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderRadius: 6,
  },
  selectedGroupHeaderActive: {
    backgroundColor: colors.primary_light,
  },
  selectedGroupTitleBtn: {
    flex: 1,
    paddingVertical: 2,
    marginRight: 8,
  },
  selectedGroupChevronBtn: {
    padding: 4,
  },
  selectedGroupTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: "#222",
    fontFamily: "Roboto",
  },
  selectedGroupTitleActive: {
    color: colors.primary,
  },
  lotTableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  lotTableRow: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  lotTableRowSelected: {
    backgroundColor: colors.primary_light,
    borderRadius: 6,
  },
  lotTh: {
    fontSize: 12,
    fontWeight: "700",
    color: "#333",
    fontFamily: "Roboto",
  },
  lotTd: {
    fontSize: 13,
    color: "#333",
    fontFamily: "Roboto",
  },
  lotColRemove: { width: "10%", alignItems: "center" },
  lotColLot: { width: "28%" },
  lotColAmt: { width: "28%", flexDirection: "row", alignItems: "center" },
  lotColLoc: { width: "34%" },
  amountInput: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 4,
    minWidth: 48,
    fontSize: 13,
    color: "#222",
  },
  unitsTxt: { marginLeft: 4, fontSize: 12, color: "#666" },
  detailsBox: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
    overflow: "visible",
    zIndex: 15,
  },
  detailsTitle: {
    fontSize: 15,
    fontWeight: "700",
    color: "#222",
    marginBottom: 10,
    fontFamily: "Roboto",
  },
  detailsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  detailsCol: { flex: 1, alignItems: "center" },
  detailsLabel: {
    fontSize: 12,
    color: "#666",
    marginTop: 2,
    marginBottom: 6,
    fontFamily: "Roboto",
  },
  detailsChip: {
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderRadius: 18,
    paddingHorizontal: 10,
    paddingVertical: 6,
    maxWidth: "100%",
    flexDirection: "row",
    alignItems: "center",
  },
  detailsChipFilled: {
    backgroundColor: colors.primary,
  },
  detailsChipTxt: {
    color: colors.primary,
    fontSize: 13,
    fontFamily: "Roboto",
    flexShrink: 1,
  },
  detailsChipTxtFilled: {
    color: "#fff",
    fontSize: 13,
    fontFamily: "Roboto",
  },
  detailsArrow: { marginHorizontal: 6 },
  toLocationErrorBanner: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.primary_light,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 10,
    marginTop: 12,
  },
  toLocationErrorIcon: {
    marginRight: 8,
  },
  toLocationErrorTxt: {
    flex: 1,
    fontSize: 13,
    color: colors.primary,
    fontFamily: "Roboto",
    lineHeight: 18,
    marginRight: 8,
  },
  toLocationErrorOkBtn: {
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    flexShrink: 0,
  },
  toLocationErrorOkTxt: {
    color: colors.primary,
    fontSize: 13,
    fontWeight: "700",
    fontFamily: "Roboto",
  },
  lotCodesInfoWrap: {
    marginTop: 12,
    position: "relative",
    zIndex: 20,
    overflow: "visible",
  },
  lotCodesInfoBanner: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f7f8fa",
    borderWidth: 1,
    borderColor: "#d0d5dd",
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  lotCodesInfoIcon: {
    marginRight: 8,
  },
  lotCodesInfoTxt: {
    flex: 1,
    fontSize: 13,
    color: colors.primary,
    fontFamily: "Roboto",
    lineHeight: 18,
    marginRight: 8,
  },
  lotCodesTipBubble: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 6,
    maxWidth: "92%",
    backgroundColor: "#4a4a4a",
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    zIndex: 30,
    elevation: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  lotCodesTipTxt: {
    color: "#fff",
    fontSize: 13,
    lineHeight: 18,
    fontFamily: "Roboto",
  },
  lotCodesTipArrow: {
    position: "absolute",
    right: 14,
    top: -6,
    width: 12,
    height: 12,
    backgroundColor: "#4a4a4a",
    transform: [{ rotate: "45deg" }],
  },
  toDropdown: {
    marginTop: 8,
    width: "100%",
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    backgroundColor: "#fff",
    overflow: "hidden",
    zIndex: 20,
  },
  toDropdownSearch: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  toDropdownInput: {
    flex: 1,
    marginLeft: 6,
    height: 32,
    fontSize: 13,
    color: "#333",
    paddingHorizontal: 8,
    paddingVertical: 0,
    fontFamily: "Roboto",
    minWidth: 0,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: 6,
    textAlignVertical: "center",
  },
  toDropdownCameraBtn: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: colors.primary,
    justifyContent: "center",
    alignItems: "center",
    marginLeft: 6,
    flexShrink: 0,
  },
  toDropdownList: {
    maxHeight: 140,
  },
  toDropdownRow: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  toDropdownRowTxt: {
    fontSize: 13,
    color: "#333",
    fontFamily: "Roboto",
  },
  emptyTxt: {
    textAlign: "center",
    color: "#888",
    padding: 16,
    fontFamily: "Roboto",
  },
});
